import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wrench, RefreshCw, Hammer, Trash2, MessageCircle, ArrowRight, ArrowLeft, CalendarDays,
  CheckCircle2, Plus, Minus,
} from "lucide-react";
import config from "../../config";
import { useCityPrice } from "../../hooks/useCityPrice";
import { DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD     = 3;
const MAX_CAMERAS    = 20;

const WORK_TYPE_ICONS = { repair: Wrench, replace: RefreshCw, install: Hammer, remove: Trash2, consultation: MessageCircle };

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const STEPS = [
  { key: "service",  label: "Service",  sub: "Repair, replace, install, remove or consult" },
  { key: "type",     label: "CCTV Setup", sub: "Type, cameras & live view"                 },
  { key: "schedule", label: "Date & Time", sub: "Book your visit"                          },
];

const CctvBookingPage = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();

  const service = useMemo(
    () => config.SERVICE_CONFIG?.technical?.services?.["electrical-services"]?.types?.find((t) => t.value === "cctv"),
    []
  );
  const cctvTypes = service?.subCategories?.[0]?.types || [];

  const [step, setStep] = useState("service");
  const [workTypeKey, setWorkTypeKey] = useState(null);
  const [cctvType, setCctvType] = useState(null);
  const [cameraCount, setCameraCount] = useState(1);
  const [liveView, setLiveView] = useState(null);
  const [note, setNote] = useState("");

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const stepIndex = STEPS.findIndex((s) =>
    s.key === (["type", "cameracount", "liveview", "note"].includes(step) ? "type" : step)
  );

  const workType = service?.workTypes?.find((wt) => wt.key === workTypeKey) || null;
  const isConsultationFlow = workTypeKey === "consultation" || cctvType?.notSure;

  const cityBasePrice = (item) => cityPrice(item.value, item.price);
  const perCameraPrice = cctvType && workType ? Math.round(cityBasePrice(cctvType) * (workType.multiplier || 1)) : 0;
  const subtotal = isConsultationFlow
    ? (service ? cityPrice(service.consultation.value, service.consultation.price) : 0)
    : perCameraPrice * cameraCount;
  const tax = Number(((subtotal * 18) / 100).toFixed(2));
  const surcharge = Number(((subtotal * 5) / 100).toFixed(2));
  const grandTotal = Number((subtotal + tax + surcharge).toFixed(2));

  const chooseWorkType = (key) => {
    setWorkTypeKey(key);
    setCctvType(null);
    setStep("type");
  };

  const chooseType = (t) => {
    setCctvType(t);
    if (workTypeKey === "consultation" || t.notSure) {
      setNote("");
      setStep("note");
    } else {
      setCameraCount(1);
      setStep("cameracount");
    }
  };

  const confirmCameraCount = () => setStep("liveview");
  const confirmLiveView = (lv) => { setLiveView(lv); setNote(""); setStep("note"); };

  const goBack = () => {
    if (step === "note") {
      if (isConsultationFlow) setStep("type");
      else setStep("liveview");
      return;
    }
    if (step === "liveview") { setStep("cameracount"); return; }
    if (step === "cameracount") { setStep("type"); return; }
    if (step === "type") { setWorkTypeKey(null); setCctvType(null); setStep("service"); return; }
    if (step === "schedule") { setStep("note"); return; }
    navigate("/schedule-booking/technical");
  };

  const goToSchedule = async () => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const liveViewLabel = service?.liveViewOptions?.find((lv) => lv.value === liveView)?.label;

  const handleCheckout = async () => {
    if (!selectedDate || !selectedTime) return;
    setCheckoutLoading(true);

    try {
      let cartItem;
      if (isConsultationFlow) {
        const c = service.consultation;
        cartItem = {
          id: `cctv-consultation-${cctvType?.value || "general"}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
          title: `CCTV Consultation${cctvType && !cctvType.notSure ? ` — ${cctvType.label}` : ""}`,
          name: `CCTV Consultation${cctvType && !cctvType.notSure ? ` — ${cctvType.label}` : ""}`,
          short: note.trim() || "No additional notes",
          subtitle: "1 visit",
          category: "CCTV",
          price: cityPrice(c.value, c.price),
          image: cctvType?.image || service?.image || DEFAULT_HERO_IMAGE,
          qty: 1,
        };
      } else {
        const label = `${workType.label} — ${cctvType.label}`;
        cartItem = {
          id: `cctv-${workTypeKey}-${cctvType.value}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
          title: label,
          name: label,
          short: [
            `${cameraCount} camera${cameraCount > 1 ? "s" : ""}`,
            liveViewLabel ? `Live view: ${liveViewLabel}` : null,
            note.trim() || null,
          ].filter(Boolean).join(" · "),
          subtitle: `${cameraCount} camera${cameraCount > 1 ? "s" : ""}`,
          category: "CCTV",
          price: perCameraPrice,
          image: cctvType.image || service?.image || DEFAULT_HERO_IMAGE,
          qty: cameraCount,
        };
      }

      const cartItems = [{
        ...cartItem,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: "technical",
      }];

      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItems[0].id) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, ...cartItems]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      // Back to the shared technical-services cart-building page (already
      // written to the shared cart above) instead of straight to checkout,
      // so other services can still be added before paying once for all.
      if (isUserLoggedIn()) {
        navigate("/schedule-booking/technical");
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/schedule-booking/technical" } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!service) {
    return <div style={S.page}><div style={S.container}><div style={S.errorBox}>CCTV service is not configured.</div></div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/schedule-booking/technical" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>CCTV</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><CalendarDays size={13} /> CCTV BOOKING</div>
          <h1 style={S.heroTitle}>Book CCTV Repair, Replace, Install, Remove or Consultation</h1>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── What do you need? (work type) ───────────────────────────────── */}
        {step === "service" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What do you need?</h2>
            <p style={S.sectionSub}>Choose the service for your CCTV</p>
            <div style={S.workTypeGrid}>
              {[...service.workTypes, { key: "consultation", label: "Consultation" }].map((wt) => {
                const Icon = WORK_TYPE_ICONS[wt.key] || Wrench;
                return (
                  <button key={wt.key} type="button" style={S.workTypeCard} onClick={() => chooseWorkType(wt.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={22} color="#16a34a" /></div>
                    <div style={S.workTypeLabel}>{wt.label}</div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Select CCTV Type ──────────────────────────────────────────────── */}
        {step === "type" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Select CCTV Type — {workType?.label || "Consultation"}</h2>
            <p style={S.sectionSub}>Pick the kind of CCTV setup you have or want</p>
            <div style={S.typeGrid}>
              {cctvTypes.map((t) => (
                <button key={t.value} type="button" style={S.typeCard} onClick={() => chooseType(t)}>
                  <img
                    src={t.image || DEFAULT_HERO_IMAGE}
                    alt={t.label}
                    style={S.typeImg}
                    onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                  />
                  <div style={S.typeBody}>
                    <div style={S.typeName}>{t.label}</div>
                    {!t.notSure && (
                      <div style={S.typePrice}>
                        From ₹{Math.round(cityBasePrice(t) * (workType?.multiplier || 1))}/camera
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Number of Cameras ─────────────────────────────────────────────── */}
        {step === "cameracount" && cctvType && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Number of Cameras</h2>
            <p style={S.sectionSub}>{workType.label} — {cctvType.label}</p>
            <div style={S.cameraCountRow}>
              {service.cameraCountOptions.map((n) => (
                <button
                  key={n} type="button"
                  style={cameraCount === n ? S.cameraCountBtnActive : S.cameraCountBtn}
                  onClick={() => setCameraCount(n)}
                >
                  {n}
                </button>
              ))}
              <div style={S.moreQtyWrap}>
                <button type="button" style={S.qtyBtn} onClick={() => setCameraCount((n) => Math.max(1, n - 1))}>
                  <Minus size={13} />
                </button>
                <span style={S.moreQtyText}>{cameraCount}</span>
                <button type="button" style={S.qtyBtnP} onClick={() => setCameraCount((n) => Math.min(MAX_CAMERAS, n + 1))}>
                  <Plus size={13} />
                </button>
                <span style={S.moreQtyLabel}>More</span>
              </div>
            </div>
            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>{cameraCount} camera{cameraCount > 1 ? "s" : ""} × ₹{perCameraPrice}</span><span>₹{(perCameraPrice * cameraCount).toFixed(2)}</span></div>
            </div>
            <button type="button" style={S.continueBtn} onClick={confirmCameraCount}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Live View ─────────────────────────────────────────────────────── */}
        {step === "liveview" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Live View</h2>
            <p style={S.sectionSub}>Where would you like to view your cameras?</p>
            <div style={S.workTypeGrid}>
              {service.liveViewOptions.map((lv) => (
                <button key={lv.value} type="button" style={S.workTypeCard} onClick={() => confirmLiveView(lv.value)}>
                  <div style={S.workTypeLabel}>{lv.label}</div>
                  <ArrowRight size={16} color="#9ca3af" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Consultation / additional notes ──────────────────────────────── */}
        {step === "note" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{isConsultationFlow ? "💬 Need Help?" : "Anything else?"}</h2>
            <p style={S.sectionSub}>
              {isConsultationFlow
                ? "Talk to a ClickNido Expert — tell us what you need"
                : "Optional — add any notes for the technician"}
            </p>
            <textarea
              style={S.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isConsultationFlow ? "E.g. I want to set up CCTV for my shop entrance and back door." : "E.g. Please call before arrival."}
              rows={4}
            />
            <button
              type="button"
              style={{ ...S.continueBtn, ...(isConsultationFlow && note.trim() === "" ? S.continueBtnDisabled : {}) }}
              disabled={isConsultationFlow && note.trim() === ""}
              onClick={goToSchedule}
            >
              {isConsultationFlow ? "Continue" : note.trim() ? "Continue" : "Skip & Continue"} <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Date & Time (2-hour slot) ─────────────────────────────────────── */}
        {step === "schedule" && (
          <>
            <div style={S.summaryBar}>
              <div style={S.summaryBody}>
                <div style={S.summaryTitle}>CCTV Booking Summary</div>
                <div style={S.summaryChipRow}>
                  {isConsultationFlow ? (
                    <span style={S.summaryChip}>CCTV Consultation{cctvType && !cctvType.notSure ? ` — ${cctvType.label}` : ""}</span>
                  ) : (
                    <>
                      <span style={S.summaryChip}>{workType.label} — {cctvType.label} × {cameraCount}</span>
                      {liveViewLabel && <span style={S.summaryChip}>Live view: {liveViewLabel}</span>}
                    </>
                  )}
                  <span style={S.summaryDurationChip}>₹{grandTotal.toFixed(2)}</span>
                </div>
                {note.trim() && <div style={S.noteBox}>"{note.trim()}"</div>}
              </div>
              <button type="button" style={S.summaryChangeBtn} onClick={goBack}>
                Change
              </button>
            </div>

            <div style={S.scheduleHead}>
              <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
              <div style={S.legend}>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
              </div>
            </div>

            <div style={S.scheduleColumns}>
              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>1. Select Date</div>
                {loadingAvailability ? (
                  <div style={S.stateBox}>Loading…</div>
                ) : availabilityError ? (
                  <div style={S.errorBox}>{availabilityError}</div>
                ) : (
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button"
                          disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                <div style={S.slotGrid}>
                  {(activeDay?.slots || []).map((slot) => {
                    const state      = getSlotState(slot);
                    const selectable = state === "available";
                    const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                    const selected   = selectedTime === slot.time;
                    return (
                      <button
                        key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        style={{
                          ...S.slotBtn,
                          ...(selected ? S.slotBtnActive : {}),
                          ...(state === "booked" ? S.slotBtnBooked : {}),
                          ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                        }}
                        onClick={() => selectable && pickTime(slot.time, slot._id)}
                      >
                        <div style={S.slotTimeRow}>
                          <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                          {selected && <CheckCircle2 size={13} color="#fff" />}
                        </div>
                        <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                          {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>Service / Labour</span><span>₹{subtotal.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>Tax &amp; Surcharge</span><span>₹{(tax + surcharge).toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>Total</span><span>₹{grandTotal.toFixed(2)}</span></div>
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!selectedTime || checkoutLoading ? S.continueBtnDisabled : {}) }}
              disabled={!selectedTime || checkoutLoading}
              onClick={handleCheckout}
            >
              {checkoutLoading ? "Proceeding..." : isUserLoggedIn() ? "Proceed to Checkout" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "660px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "23px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },

  workTypeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" },
  workTypeCard: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  workTypeIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  workTypeLabel: { fontSize: "15px", fontWeight: 800, color: "#111827", flex: 1 },

  typeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" },
  typeCard: { background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", cursor: "pointer", padding: 0, textAlign: "left" },
  typeImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f0f2fa" },
  typeBody: { padding: "10px 12px 12px" },
  typeName: { fontSize: "13.5px", fontWeight: 800, color: "#111827", marginBottom: "4px" },
  typePrice: { fontSize: "12.5px", fontWeight: 700, color: "#16a34a" },

  cameraCountRow: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "18px" },
  cameraCountBtn: { width: "52px", height: "52px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "16px", fontWeight: 800, color: "#111827", cursor: "pointer" },
  cameraCountBtnActive: { width: "52px", height: "52px", borderRadius: "12px", border: "1.5px solid #16a34a", background: "#16a34a", fontSize: "16px", fontWeight: 800, color: "#fff", cursor: "pointer" },
  moreQtyWrap: { display: "flex", alignItems: "center", gap: "8px", marginLeft: "8px" },
  moreQtyText: { minWidth: "22px", textAlign: "center", fontSize: "15px", fontWeight: 800, color: "#111827" },
  moreQtyLabel: { fontSize: "12px", color: "#9ca3af", marginLeft: "4px" },

  qtyBtn: { width: "30px", height: "30px", borderRadius: "50%", border: "1px solid #e4e7ef", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", flexShrink: 0 },
  qtyBtnP: { width: "30px", height: "30px", borderRadius: "50%", border: "none", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 },

  textarea: { width: "100%", minHeight: "100px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontFamily: "inherit", fontSize: "13px", resize: "vertical", marginBottom: "16px", boxSizing: "border-box" },
  noteBox: { fontSize: "12px", color: "#6b7280", background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "10px", padding: "10px 12px", marginTop: "10px", lineHeight: 1.4 },

  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
  summaryBody: { minWidth: 0, flex: 1 },
  summaryTitle: { fontSize: "15px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
};

export default CctvBookingPage;
